/* ============================================================
   STALE-CHUNK RELOAD — survive a deploy with the tab still open.
   ------------------------------------------------------------
   Vite fingerprints every lazy chunk (bump scenes, channel
   components). A deploy rotates the hashes, so an open tab's next
   dynamic import() 404s — "Failed to fetch dynamically imported
   module" — and whatever was waiting on it (e.g. a channel bumper)
   freezes. The only real fix is to reload and pick up the fresh
   index.html + chunk map. Guarded by a cooldown so a genuinely
   missing chunk can't spin into a reload loop.
   ============================================================ */
const FLAG = 'orion:chunk-reloaded-at'
const COOLDOWN_MS = 30_000

const isStaleChunkError = (msg: string | undefined | null): boolean =>
  !!msg &&
  (/failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg))

const reloadOnce = () => {
  let last = 0
  try { last = Number(sessionStorage.getItem(FLAG)) || 0 } catch { /* sessionStorage may be blocked */ }
  // already reloaded moments ago → the chunk is genuinely gone (broken deploy); don't loop.
  if (Date.now() - last < COOLDOWN_MS) return
  try { sessionStorage.setItem(FLAG, String(Date.now())) } catch { /* ignore */ }
  window.location.reload()
}

/** Install the listeners. Call once at boot (main.tsx), before anything lazy-loads. */
export function installChunkReload() {
  // Vite dispatches this when a lazy chunk's preload/import fails — the canonical signal.
  window.addEventListener('vite:preloadError', (e) => { e.preventDefault(); reloadOnce() })
  // Belt-and-suspenders for a raw import() rejection that didn't surface as a preloadError.
  window.addEventListener('unhandledrejection', (e) => {
    const r = e.reason
    if (isStaleChunkError(typeof r === 'string' ? r : r?.message)) reloadOnce()
  })
  window.addEventListener('error', (e) => { if (isStaleChunkError(e.message)) reloadOnce() })
}
